import React, { useState, useEffect, useContext } from 'react';
import {
    Drawer,
    Typography,
    Box,
    IconButton,
    Grid,
    TextField,
    Button,
    Divider,
    MenuItem,
    FormControlLabel,
    Checkbox
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { appStyles } from '../../styles';
import { TimeSelector } from '../Forms';
import AuthUserContext from '../Session/authUserContext';
import { ROLES, SORTIE_LOCKS, EVENT_STATUS } from '../../constants';
import { addHours } from 'date-fns';

const SortieSidebar = (props) => {
    const { open, onClose, flight, sortieDefaults, sortieEvents, onSave } = props;
    const authUser = useContext(AuthUserContext);
    const [sortieType, setSortieType] = useState('');
    const [takeoffTime, setTakeoffTime] = useState(0);
    const [landTime, setLandTime] = useState(0);
    const [selectedEvents, setSelectedEvents] = useState({});

    // Locking Logic
    const [canEdit, setCanEdit] = useState(true);

    useEffect(() => {
        if (flight && authUser) {
            setSortieType(flight.sortieType);
            setTakeoffTime(flight.takeoffTime); 
            setLandTime(flight.landTime);
            
            // Calculate Locks
            const tTime = new Date(flight.takeoffTime);
            const now = new Date();
            const schedLocked = tTime < addHours(now, SORTIE_LOCKS.SCHED);
            const adminLocked = tTime < addHours(now, SORTIE_LOCKS.ADMIN);
            
            const canEditData = !adminLocked && (!schedLocked || authUser.role === ROLES.ADMIN) && authUser.role > ROLES.MEMBER;
            const flightHasStatus = flight.status !== undefined && EVENT_STATUS[flight.status] !== EVENT_STATUS.UNKNOWN;
            
            setCanEdit(canEditData && !flightHasStatus);

            // Events initialization ...
            const currentEvents = {};
            if (flight.events) {
                Object.values(flight.events).forEach(e => {
                     if (e.status !== 'CANCELLED') { 
                         // Store details if present, or initialize empty
                         currentEvents[e.type] = {
                             selected: true,
                             location: e.location || '',
                             blockStart: e.blockStart || flight.takeoffTime,
                             blockEnd: e.blockEnd || flight.landTime
                         };
                     }
                });
            }
            setSelectedEvents(currentEvents);
        }
    }, [flight, authUser]);

    const handleEventToggle = (eventId) => {
        setSelectedEvents(prev => {
            const isSelected = !!prev[eventId]?.selected;
            if (isSelected) {
                // Deselecting
                 return {
                    ...prev,
                    [eventId]: {
                        ...prev[eventId],
                        selected: false
                    }
                };
            } else {
                // Selecting
                return {
                    ...prev,
                    [eventId]: {
                        ...prev[eventId],
                        selected: true,
                        // Initialize defaults if undefined
                        location: prev[eventId]?.location || '',
                        blockStart: prev[eventId]?.blockStart || takeoffTime,
                        blockEnd: prev[eventId]?.blockEnd || landTime
                    }
                };
            }
        });
    };

    const handleEventDetailChange = (eventId, field, value) => {
        setSelectedEvents(prev => ({
            ...prev,
            [eventId]: {
                ...prev[eventId],
                [field]: value
            }
        }));
    };

    const handleSave = () => {
        if (onSave) {
            // Reconstruct events object
            // This is tricky because we might need to preserve existing event IDs or create new ones.
            // For simplicity, if we check an event, we ensure it exists in the update.
            // If unchecked, we might set status to CANCELLED or remove it?
            // The requirement says "adding or removing events".
            // Let's assume for now: Checked = Status: 'SCHEDULED' (if not present) or keep existing status.
            // Unchecked = Remove or Cancel? Usually remove if not flown.
            
            // Let's return the full flight object structure expected.
            const newEvents = {};
            // Copy existing that are still selected
            if (flight.events) {
                 Object.entries(flight.events).forEach(([k, v]) => {
                     const eventState = selectedEvents[v.type];
                     if (eventState && eventState.selected) {
                         newEvents[k] = {
                             ...v,
                             location: eventState.location,
                             blockStart: eventState.blockStart,
                             blockEnd: eventState.blockEnd
                         };
                     }
                 });
            }
            
            // Add new ones
            Object.keys(selectedEvents).forEach(typeId => {
                const eventState = selectedEvents[typeId];
                if (eventState && eventState.selected) {
                    // Check if already in newEvents
                    const exists = Object.values(newEvents).some(e => e.type === typeId);
                    if (!exists) {
                        const newKey = Date.now() + typeId; 
                        newEvents[newKey] = {
                            type: typeId,
                            status: 'SCHEDULED',
                            location: eventState.location,
                            blockStart: eventState.blockStart,
                            blockEnd: eventState.blockEnd
                        };
                    }
                }
            });
            
            onSave({
                ...flight,
                sortieType,
                takeoffTime,
                landTime,
                events: newEvents
            });
        }
    };

    const hasValidationError = Object.values(selectedEvents).some(e => e.selected && (!e.location || e.location.trim() === ''));
    const isSaveDisabled = !canEdit || hasValidationError;

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <div style={{ width: 400, padding: 20 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Edit Sortie</Typography>
                    <IconButton onClick={onClose}>
                        <Close />
                    </IconButton>
                </Box>
                <Divider />
                <Box mt={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                select
                                label="Sortie Type"
                                value={sortieType}
                                onChange={(e) => setSortieType(e.target.value)}
                                fullWidth
                                disabled={!canEdit}
                            >
                                {Object.keys(sortieDefaults).map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TimeSelector
                                label="Takeoff"
                                value={takeoffTime}
                                onChange={setTakeoffTime}
                                disabled={!canEdit}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TimeSelector
                                label="Land"
                                value={landTime}
                                onChange={setLandTime}
                                disabled={!canEdit}
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">Events</Typography>
                            {sortieEvents && Object.entries(sortieEvents).map(([id, event]) => {
                                const isSelected = !!selectedEvents[id]?.selected;
                                return (
                                    <Box key={id} mb={1}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => handleEventToggle(id)}
                                                    name={event.name}
                                                    color="primary"
                                                    disabled={!canEdit}
                                                />
                                            }
                                            label={event.name}
                                        />
                                        {isSelected && (
                                            <Box ml={4}>
                                                <TextField
                                                    id={`location-${id}`}
                                                    label="Location"
                                                    value={selectedEvents[id]?.location || ''}
                                                    onChange={(e) => handleEventDetailChange(id, 'location', e.target.value)}
                                                    margin="dense"
                                                    fullWidth
                                                    required
                                                    error={!selectedEvents[id]?.location}
                                                    disabled={!canEdit}
                                                />
                                                <Grid container spacing={1}>
                                                    <Grid item xs={6}>
                                                        <TimeSelector
                                                            label="Block Start"
                                                            value={selectedEvents[id]?.blockStart || 0}
                                                            onChange={(val) => handleEventDetailChange(id, 'blockStart', val)}
                                                            disabled={!canEdit}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <TimeSelector
                                                            label="Block End"
                                                            value={selectedEvents[id]?.blockEnd || 0}
                                                            onChange={(val) => handleEventDetailChange(id, 'blockEnd', val)}
                                                            disabled={!canEdit}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Grid>
                    </Grid>
                </Box>
                <Box mt={4} display="flex" justifyContent="flex-end">
                     <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaveDisabled}>
                        Save
                    </Button>
                </Box>
            </div>
        </Drawer>
    );
};

export default withStyles(appStyles)(SortieSidebar);
